import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import supabase from '../supabase/supabase';
import { getVertical } from '../common/verticals';
import { useCollective } from '../context/CollectiveContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiExternalLink, FiMapPin, FiRefreshCw, FiGrid } = FiIcons;

const TABLE_CITIES = 'cities_1715634000000';
const TABLE_SLOTS  = 'slots_1715634000000';

function VendorCard({ slot, v }) {
  return (
    <div className="bg-white border-4 border-brand-brown/10 rounded-[2rem] overflow-hidden hover:border-brand-blue hover:shadow-[8px_8px_0px_#009FE3] transition-all group flex flex-col" style={{ '--v-hex': v.hex }}>
      {/* Ad image */}
      <div className="aspect-[4/3] bg-brand-cream/60 overflow-hidden relative">
        {slot.ad_image_url ? (
          <img
            src={slot.ad_image_url}
            alt={slot.category}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-brand-brown/20">
            <SafeIcon icon={FiGrid} className="text-5xl" />
            <span className="text-xs font-black uppercase tracking-widest">Ad Coming Soon</span>
          </div>
        )}
        {/* Category pill */}
        <span
          className="absolute top-3 left-3 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow"
          style={{ backgroundColor: v.hex }}
        >
          {slot.category}
        </span>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-black text-brand-brown uppercase tracking-tight text-lg leading-tight mb-2 group-hover:text-brand-blue transition-colors">
          {slot.category}
        </h3>
        {slot.description && (
          <p className="text-brand-brown/60 font-bold text-sm leading-relaxed mb-4 flex-1">
            {slot.description}
          </p>
        )}
        {slot.website_url && (
          <a
            href={slot.website_url.startsWith('http') ? slot.website_url : `https://${slot.website_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto inline-flex items-center gap-2 text-white font-black uppercase text-xs px-5 py-3 rounded-xl transition-all hover:opacity-90"
            style={{ backgroundColor: v.hex }}
          >
            <SafeIcon icon={FiExternalLink} /> Visit Website
          </a>
        )}
      </div>
    </div>
  );
}

export default function Directory() {
  const { citySlug: paramSlug } = useParams();
  const collective = useCollective();

  // In collective mode use the domain's citySlug; otherwise use the URL param
  const citySlug = collective ? collective.citySlug : paramSlug;

  const [city, setCity]   = useState(null);
  const [slots, setSlots] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!citySlug) return;
    setLoading(true);

    supabase
      .from(TABLE_CITIES)
      .select('*')
      .eq('slug', citySlug)
      .single()
      .then(async ({ data: cityData }) => {
        if (!cityData) { setLoading(false); return; }
        setCity(cityData);
        const { data: slotData } = await supabase
          .from(TABLE_SLOTS)
          .select('*')
          .eq('city_id', cityData.id)
          .eq('is_reserved', true)
          .order('side')
          .order('position');
        setSlots(slotData ?? []);
        setLoading(false);
      });
  }, [citySlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center pt-24">
        <SafeIcon icon={FiRefreshCw} className="animate-spin text-4xl text-brand-blue" />
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center pt-24">
        <p className="font-black text-brand-brown/40 uppercase tracking-widest">Collective not found</p>
      </div>
    );
  }

  const v = getVertical(city.vertical);
  const filtered = slots.filter(s =>
    s.category?.toLowerCase().includes(query.toLowerCase()) ||
    s.description?.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-brand-cream pt-24 pb-20">

      {/* Header */}
      <div className="text-white py-16 px-4 text-center" style={{ backgroundColor: v.hex }}>
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-black uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            <SafeIcon icon={FiMapPin} /> {city.name}, {city.state ?? 'AZ'}
          </div>
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter italic leading-none mb-4">
            {v.collective}
          </h1>
          <p className="text-white/70 font-bold text-lg max-w-xl mx-auto">
            {v.tagline} — all in one place, all serving {city.name}.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 mt-12">

        {/* Search bar */}
        <div className="relative max-w-xl mx-auto mb-12">
          <SafeIcon icon={FiSearch} className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-brown/30 text-xl" />
          <input
            type="text"
            placeholder="Search vendors or categories…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full bg-white border-4 border-brand-brown/10 rounded-2xl pl-14 pr-6 py-4 font-bold text-brand-brown outline-none focus:border-brand-blue transition-colors shadow-sm"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-brand-brown/30 font-black uppercase tracking-widest text-lg">
              {query ? 'No vendors match your search' : 'No vendors yet — spots are filling up soon'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-center text-brand-brown/40 font-black uppercase tracking-widest text-xs mb-8">
              {filtered.length} vendor{filtered.length !== 1 ? 's' : ''} in this collective
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filtered.map(slot => (
                <VendorCard key={slot.id} slot={slot} v={v} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
